/** Copyright (c) 2026 PhiloFund. All rights reserved. */
/**
 * ProfileMenu.tsx — the header account drill-in (PF.BACKLOG.30).
 *
 * The rich content shown when the AppBar account button is clicked. Rendered
 * inside a Popover so AppShell stays lean. It is a LAUNCHER into full profile
 * management (SettingsPage et al.), NOT a duplicate of the edit UI:
 *
 *   - Identity header (PROFILE-MODEL §2): avatar, display name, email, role chip.
 *   - Change photo: a camera button on the avatar opens a hidden file input,
 *     reads the chosen image to an object URL, and updates the auth store's
 *     photoURL so the new avatar shows everywhere immediately. Demo-only —
 *     the URL lives in this browser session, nothing is uploaded.
 *   - Role-aware links to self-profile management: profile & settings, change
 *     password, security (2FA & passkeys), sign out.
 *   - A subtle note that privilege-gated fields (email, role) can't be changed
 *     here (PROFILE-MODEL §3/§5) — settings shows them locked-with-reason.
 *
 * Accessibility: the Popover traps focus; every action is a real button/link
 * (keyboard-operable); the camera control and file input carry aria labels.
 */
import React, { useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuthStore } from '@/stores/auth.store';
import { COLOR } from '@/theme/tokens';
import type { UserRole } from '@philofund/shared/types/user';

// ---------------------------------------------------------------------------
// Role metadata: chip label + color + the role's own settings destination.
// ---------------------------------------------------------------------------

type ChipColor = 'primary' | 'secondary' | 'info';

interface RoleMeta {
  /** Human label shown in the identity chip. */
  label: string;
  /** MUI chip color — mirrors the role accent used across the shell. */
  color: ChipColor;
  /** Route to this role's "My profile & settings" surface. */
  settingsRoute: string;
}

const ROLE_META: Record<UserRole, RoleMeta> = {
  donor: { label: 'Donor', color: 'primary', settingsRoute: '/donor/settings' },
  org_admin: {
    label: 'Organization Admin',
    color: 'secondary',
    settingsRoute: '/organization/settings',
  },
  platform_admin: {
    label: 'PhiloFund Administration',
    color: 'info',
    settingsRoute: '/admin/settings',
  },
};

const CHANGE_PASSWORD_ROUTE = '/auth/password/change';
const SECURITY_ROUTE = '/auth/mfa/enroll';

// ---------------------------------------------------------------------------
// User-object accessors (the store holds a Firebase User-shaped object).
// ---------------------------------------------------------------------------

function readString(user: unknown, key: string): string {
  if (user !== null && typeof user === 'object') {
    const v = (user as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
  }
  return '';
}

function readRole(user: unknown): UserRole {
  const role = readString(user, 'appRole');
  if (role === 'org_admin' || role === 'platform_admin' || role === 'donor') {
    return role;
  }
  return 'donor';
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProfileMenuProps {
  /** Element the popover anchors to (the account button). */
  anchorEl: HTMLElement | null;
  /** Whether the popover is open. */
  open: boolean;
  /** Close the popover. */
  onClose: () => void;
  /** Sign the current user out (owned by AppShell). */
  onSignOut: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProfileMenu({
  anchorEl,
  open,
  onClose,
  onSignOut,
}: ProfileMenuProps): JSX.Element {
  const { user, setAvatarUrl } = useAuthStore();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Holds the last object URL we minted so we can revoke it when replaced,
  // avoiding a leak across repeated demo uploads.
  const lastObjectUrl = useRef<string | null>(null);

  const displayName = readString(user, 'displayName');
  const email = readString(user, 'email');
  const photoURL = readString(user, 'photoURL');
  const role = readRole(user);
  const meta = ROLE_META[role];

  // Primary identity line falls back to the email, mirroring the header greeting
  // helper — so the account line is never blank even without a display name.
  const primaryLine =
    displayName !== '' ? displayName : email !== '' ? email : 'Your account';
  // Only show the email as a secondary line when it isn't already the primary.
  const showEmailLine = displayName !== '' && email !== '';

  const initial =
    displayName.trim().charAt(0).toUpperCase() ||
    email.trim().charAt(0).toUpperCase() ||
    '?';

  const pickPhoto = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset the input so re-selecting the same file fires change again.
      event.target.value = '';
      if (file === undefined) return;
      if (lastObjectUrl.current !== null) {
        URL.revokeObjectURL(lastObjectUrl.current);
      }
      const objectUrl = URL.createObjectURL(file);
      lastObjectUrl.current = objectUrl;
      setAvatarUrl(objectUrl);
    },
    [setAvatarUrl],
  );

  const handleSignOut = useCallback(() => {
    onClose();
    onSignOut();
  }, [onClose, onSignOut]);

  return (
    <Popover
      id="profile-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { width: 320, mt: 1, borderRadius: 2 } } }}
    >
      {/* ---- Identity header (PROFILE-MODEL §2) ---------------------------- */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', gap: 1.5 }}>
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            src={photoURL !== '' ? photoURL : undefined}
            alt={displayName !== '' ? displayName : 'Account avatar'}
            sx={{ width: 56, height: 56, bgcolor: COLOR.primary.main }}
          >
            {initial}
          </Avatar>

          {/* Change-photo affordance overlaid on the avatar. */}
          <Tooltip title="Change photo" arrow>
            <IconButton
              size="small"
              onClick={pickPhoto}
              aria-label="Change profile photo"
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                bgcolor: COLOR.background.default,
                border: `1px solid ${COLOR.divider}`,
                p: 0.5,
                '&:hover': { bgcolor: COLOR.primary.lighter },
              }}
            >
              <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {/* Hidden file input driven by the camera button. */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onFileChange}
            aria-label="Upload profile photo"
            style={{ display: 'none' }}
          />
        </Box>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, lineHeight: 1.2 }}
            noWrap
            data-testid="user-display-name"
          >
            {primaryLine}
          </Typography>
          {showEmailLine && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {email}
            </Typography>
          )}
          <Chip
            label={meta.label}
            color={meta.color}
            size="small"
            sx={{ mt: 0.75, fontWeight: 600 }}
          />
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ px: 2, pb: 1.5 }}
      >
        Demo: photo is stored in this session only.
      </Typography>

      <Divider />

      {/* ---- Role-aware self-profile management links --------------------- */}
      <MenuList sx={{ py: 0.5 }}>
        <MenuItem component={RouterLink} to={meta.settingsRoute} onClick={onClose}>
          <ListItemIcon>
            <ManageAccountsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="My profile & settings" />
        </MenuItem>

        <MenuItem component={RouterLink} to={CHANGE_PASSWORD_ROUTE} onClick={onClose}>
          <ListItemIcon>
            <VpnKeyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Change password" />
        </MenuItem>

        <MenuItem component={RouterLink} to={SECURITY_ROUTE} onClick={onClose}>
          <ListItemIcon>
            <SecurityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Security (2FA & passkeys)" />
        </MenuItem>
      </MenuList>

      <Divider />

      {/* ---- Privilege-gated note (PROFILE-MODEL §3/§5) ------------------- */}
      <Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, mt: '2px', color: 'text.disabled' }} />
        <Typography variant="caption" color="text.secondary">
          Some fields (email, role) need verification or admin privileges to
          change — open{' '}
          <RouterLink
            to={meta.settingsRoute}
            onClick={onClose}
            style={{ color: COLOR.primary.main, fontWeight: 600 }}
          >
            settings
          </RouterLink>{' '}
          to see why they&apos;re locked.
        </Typography>
      </Box>

      <Divider />

      <MenuList sx={{ py: 0.5 }}>
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </MenuList>
    </Popover>
  );
}
